import type { ReactElement, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { App } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';
import { EntityHeader } from '../../../components/entity-detail/EntityHeader.js';
import { useDeleteCompanyMutation } from '../hooks/useCompanyMutations.js';
import type { CompanyDetail } from '../api/companies.client.js';

/** Сайт без протокола («example.com») иначе открылся бы относительной ссылкой. */
export function companyWebsiteHref(website: string): string {
  return /^https?:\/\//i.test(website) ? website : `https://${website}`;
}

export function CompanyDetailHeader({
  company,
  onEdit,
  onNote,
  onHistory,
}: {
  company: CompanyDetail;
  onEdit: () => void;
  onNote: () => void;
  onHistory: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const remove = useDeleteCompanyMutation();
  const website = company.website?.trim();
  const domain = company.domain?.trim();
  const industry = company.industry?.trim();
  const parts: ReactNode[] = [
    ...(industry ? [industry] : []),
    ...(website
      ? [
          <a
            key="website"
            className="company-subtitle-link"
            href={companyWebsiteHref(website)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {domain || website}
          </a>,
        ]
      : domain
        ? [domain]
        : []),
  ];
  return (
    <EntityHeader
      backTo="/companies"
      backLabel={t('crm.features.features.companies.pages.companydetailpage.к_компаниям')}
      title={company.name}
      subtitle={
        parts.length ? (
          <span data-testid="company-header-subtitle">
            {parts.map((part, index) => (
              <span key={index}>
                {index > 0 ? ' · ' : null}
                {part}
              </span>
            ))}
          </span>
        ) : null
      }
      primary={{
        key: 'edit',
        testId: 'company-edit',
        label: t('crm.entity_detail.edit'),
        onClick: onEdit,
      }}
      overflow={[
        { key: 'note', label: t('crm.deal_card.quick_add_note'), onClick: onNote },
        { key: 'history', label: t('crm.entity_detail.history'), onClick: onHistory },
        {
          key: 'delete',
          label: t('crm.features.features.companies.pages.companydetailpage.удалить'),
          danger: true,
          disabled: remove.isPending,
          confirm: {
            okText: t('crm.shared.delete'),
            cancelText: t('crm.common.cancel'),
            title: t('crm.features.features.companies.pages.companydetailpage.удалить_компанию'),
            description: t(
              'crm.features.features.companies.pages.companydetailpage.связанные_контакты_сделки_задачи_и_докумен',
            ),
          },
          onClick: async () => {
            try {
              await remove.mutateAsync(company.id);
              navigate('/companies');
            } catch (error) {
              const detail = error instanceof Error && error.message ? `: ${error.message}` : '';
              void message.error(t('crm.shared.delete_error', { detail }));
              throw error;
            }
          },
        },
      ]}
    />
  );
}
